import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useReducer, useState } from "react";

import { Fieldset, Form, Title } from "./SignupStyles";
import Input from "./ui/Input";
import Button from "./ui/Button";
import ErrorMessage from "./FormElements/ErrorMessage";
import Checkbox from "./ui/Checkbox";
import OAuthButtons from "./OAuthButtons/OAuthButtons";
import { useLocation, useSearchParams } from "react-router-dom";
import ConfigService from "@/components/service/ConfigService.js";
import CSRFInput from "@/components/CSRFInput.jsx";

const SIGNIN_STASH_KEY = "signin-form-stash";

const SignInError = () => {
  const [searchParams] = useSearchParams();

  const formatErrorMessage = () => {
    if (SPRING_SECURITY_LAST_EXCEPTION) {
      if (SPRING_SECURITY_LAST_EXCEPTION.includes("BadCredentialsException")) {
        return {
          id: "message.bad_credentials",
          defaultMessage:
            "The email address and password combination you entered is not correct. Please try again.",
        };
      }
      if (SPRING_SECURITY_LAST_EXCEPTION.includes("locked_account")) {
        return {
          id: "message.locked_account",
          defaultMessage:
            "This account has exceeded the number of allowed login attempts. Please try again later.",
        };
      }
      return SPRING_SECURITY_LAST_EXCEPTION;
    }
    return "Something went wrong";
  };

  return (
    <>
      <ErrorMessage
        error={
          (searchParams.get("message") &&
            searchParams.get("message").message[0]) ||
          (searchParams.get("error_msg") && searchParams.get("error_msg")[0])
        }
      />
      <ErrorMessage
        error={
          searchParams.get("error") !== null &&
          searchParams.get("error") !== undefined
            ? formatErrorMessage()
            : ""
        }
      />
    </>
  );
};

function SignIn() {
  const intl = useIntl();
  const emailLabel = intl.formatMessage({
    id: "template.email",
    defaultMessage: "Email",
  });
  const passwordLabel = intl.formatMessage({
    id: "template.password",
    defaultMessage: "Password",
  });
  const { search } = useLocation(); // Gets the current query string (e.g., "?page=2&filter=abc")
  const configService = ConfigService.getInstance();

  const [formFields, setFormFields] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      username: "",
      password: "",
      remember_email: false,
    },
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [html5error, setHtml5error] = useState({});
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    document.title = intl.formatMessage({
      id: "auth.docTitleSignin",
      defaultMessage: "Sign In - NewsGuard",
    });
  }, []);

  // Sign-in is a native form POST; on a failed attempt Spring Security
  // redirects back with ?error and the reload wipes all field state. Stash
  // the email + "remember my email" choice (never the password) before submitting and restore them
  // when we land back here with an error, so the user only re-enters their password.
  // One-shot: cleared on read so a later fresh visit starts blank.
  useEffect(() => {
    const stash = sessionStorage.getItem(SIGNIN_STASH_KEY);
    if (stash) {
      sessionStorage.removeItem(SIGNIN_STASH_KEY);
      const params = new URLSearchParams(search);
      if (params.get("error") !== null || params.get("error_msg") !== null) {
        try {
          setFormFields(JSON.parse(stash));
        } catch {
          // Corrupted stash — ignore and start blank.
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event) => {
    const target = event.target;
    const inputName = target.name;

    let inputValue = target.value;

    if (inputName === "remember_email") {
      inputValue = target.checked;
    }
    setHtml5error((prevState) => ({
      ...prevState,
      [inputName]: undefined,
    }));
    setFormFields({ [inputName]: inputValue });
  };

  const handleSubmit = (event) => {
    setLoading(true);
    setError(null);
    setHtml5error({});

    event.preventDefault();

    if (validate(event)) {
      // to do : save the email if the remember_email is set.

      const { password, ...safeFields } = formFields;
      sessionStorage.setItem(SIGNIN_STASH_KEY, JSON.stringify(safeFields));
      event.target.submit();
    } else {
      setLoading(false);
      // Move focus to the first invalid field (noValidate suppresses the
      // browser's native focus-first-error behavior).
      event.target.querySelector(":invalid")?.focus();
    }
  };

  const handlePttSubmit = (event) => {
    setLoading(true);
    setError(null);
    setHtml5error({});

    event.preventDefault();
    event.target.submit();
  };

  const checkIsValid = (e) => {
    if (e.target.checkValidity() === false) {
      e.target.parentNode.classList.add("was-validated");
    }
  };

  const validate = (event) => {
    setValidated(true);
    return event.target.checkValidity();
  };

  return (
    <div className="600:w-[480px] mx-auto mt-12 flex flex-col pb-12 px-3xl">
      <Title>
        <FormattedMessage id="template.signin" defaultMessage="Sign in" />
      </Title>
      <OAuthButtons />
      <p className="text-center my-4 lowercase">
        <FormattedMessage id="template.or" defaultMessage="OR" />
      </p>
      <SignInError />
      <Form
        className={`${validated ? "was-validated" : ""}`}
        onSubmit={handleSubmit}
        noValidate
        id="signin-form"
        method="post"
      >
        <Fieldset
          disabled={loading}
          aria-busy={loading}
          className="flex flex-col gap-xl"
        >
          <div className="relative">
            <Input
              type="email"
              label={emailLabel}
              placeholder={emailLabel}
              id="username"
              name="username"
              formField
              value={formFields.username}
              onChange={handleInputChange}
              onBlur={checkIsValid}
              required
              onInvalid={() => {
                let email = {
                  id: "template.email_invalid_message",
                  defaultMessage: "Please enter a valid email address",
                  name: "username",
                };
                setHtml5error((prevState) => ({
                  ...prevState,
                  email,
                }));
              }}
              error={html5error?.email}
              autoComplete="email"
            />
          </div>
          <div className="relative">
            <Input
              type="password"
              id="password"
              name="password"
              formField
              label={passwordLabel}
              placeholder={passwordLabel}
              value={formFields.password}
              onChange={handleInputChange}
              onBlur={checkIsValid}
              autoComplete="current-password"
              required
              onInvalid={() => {
                let passwordMsg = {
                  id: "message.password_length_instruction",
                  defaultMessage:
                    "Please enter a password that is 8 characters or longer",
                  name: "password",
                };
                setHtml5error((prevState) => ({
                  ...prevState,
                  password: passwordMsg,
                }));
              }}
              error={html5error.password}
              minLength={8}
            />
          </div>
          <div className="600:m-0 mr-2 mb-1 flex pb-6">
            <label
              className="flex items-center gap-[10px] text-sm"
              htmlFor="remember_email"
            >
              <Checkbox
                id="remember_email"
                name="remember_email"
                onChange={handleInputChange}
                checked={formFields.remember_email}
              />
              <FormattedMessage
                id="template.remember_my_email"
                defaultMessage="Remember my email"
              />
            </label>
          </div>
          {error ? <ErrorMessage error={error} /> : null}

          <p className="-mt-md mb-lg text-left text-xs italic text-text-placeholder-light dark:text-text-placeholder-dark">
            <FormattedMessage
              id="template.all_field_req"
              defaultMessage="All fields are required"
            />
          </p>
          <Button type="submit" isLoading={loading} className="mb-3 w-full">
            <FormattedMessage id="template.signin" defaultMessage="Sign in" />
          </Button>

          <div className="flex justify-between [&_p:last-child]:text-right">
            <p className="text-[14px]">
              <a
                href={`${configService.formulateContextPath("/forgot-password")}${search}`}
                className="link"
              >
                <FormattedMessage
                  id="template.forgot_password"
                  defaultMessage="Forgot Password?"
                />
              </a>
            </p>
            {
              <p className="text-[14px]">
                <FormattedMessage id="template.new_user" defaultMessage="New User?" />{" "}
                <a
                  href={`${configService.formulateContextPath("/register")}${search}`}
                  className="link"
                >
                  <FormattedMessage id="template.signup" defaultMessage="Sign Up" />
                </a>
              </p>
            }
          </div>
        </Fieldset>

        <CSRFInput />
      </Form>
    </div>
  );
}

export default SignIn;
